// src/services/referralService.js
//
// Phase 5 (PLAN1.md §K.2) — Tenant referral loop.
// -----------------------------------------------------------------------
// "Every tenant that signs up gets a referral code. A tenant that brings
//  3 paying tenants unlocks 6 months free."
//
// Data model:
//   tenants/{id}.referralCode   string  — 6-char human-readable code,
//                                         unique per deployment; rendered
//                                         on the admin console and
//                                         embedded in share URLs.
//   referrals/{id} = {
//     referrerTenantId,   // the tenant whose code was used
//     referredTenantId,   // the tenant that just signed up
//     referralCode,       // the code that matched (denormalised for audit)
//     status,             // 'pending' | 'paying' | 'cancelled'
//     createdAt,          // server ts — when the referred tenant signed up
//     convertedAt,        // server ts — when status → 'paying' (null else)
//   }
//
// Rules model:
//   - A signed-in user can READ the tenant doc they're scoped to (already
//     true), and from there read their own referralCode.
//   - A signed-in user can LOOK UP a tenant by referralCode during signup
//     (rules allow tenants read for signed-in users).
//   - The `referrals` collection is admin-read only; writes go through
//     Cloud Functions (same pattern as auditLog). For the Phase-5 MVP the
//     status transitions to `paying` are triggered by the Stripe webhook
//     (future work — K.1). We still allow the signup flow to CREATE a
//     `pending` referral directly from the client so the link is recorded
//     the moment the tenant is born, not after a Stripe confirmation.
// -----------------------------------------------------------------------

import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { referralService as referralServiceV2 } from './referralServiceV2';

const AUTH_BACKEND = process.env.REACT_APP_AUTH_BACKEND || 'firebase';
const TENANTS = 'tenants';
const REFERRALS = 'referrals';
// Public 1:1 index mapping referralCode → tenantId. Storing this in a
// separate collection keeps `tenants/{id}` reads locked down to the
// owning tenant while still letting an unrelated signup flow resolve a
// code at signup time. Only the tenantId + a display name leak.
const CODES = 'referralCodes';

// 6-char code — 32 characters × 6 positions = ~10^9 combinations, enough
// for the foreseeable horizon without needing uniqueness-retry round trips.
// Excludes 0/O/1/I to make the code unambiguous when read aloud.
const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

const randomChar = () =>
  ALPHABET.charAt(Math.floor(Math.random() * ALPHABET.length));

/**
 * Generate a fresh 6-character referral code. Caller checks uniqueness
 * against the `tenants.referralCode` field before using it.
 */
export const generateReferralCode = (length = 6) => {
  let out = '';
  for (let i = 0; i < length; i += 1) out += randomChar();
  return out;
};

/** Normalise user input (copy-paste from chat apps drops whitespace/dashes). */
export const normaliseReferralCode = (raw) => {
  if (typeof raw !== 'string') return '';
  return raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
};

/**
 * Build the share link for a referral code. Caller passes the app origin
 * so this helper stays pure (no `window` access — testable in node).
 */
export const buildReferralLink = (origin, code) => {
  if (!origin || !code) return '';
  const o = origin.replace(/\/$/, '');
  return `${o}/signup?ref=${encodeURIComponent(code)}`;
};

/** Copy the referral link to the clipboard (browser only, best-effort). */
export const copyToClipboard = async (text) => {
  if (typeof navigator === 'undefined' || !navigator.clipboard) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (_) {
    return false;
  }
};

const referralServiceFirebase = {
  /**
   * Pick a code that isn't already in use. We retry a bounded number of
   * times so an extraordinary run of collisions can't spin forever.
   */
  async pickUniqueCode({ maxAttempts = 6 } = {}) {
    for (let i = 0; i < maxAttempts; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const code = generateReferralCode();
      // eslint-disable-next-line no-await-in-loop
      const existing = await referralServiceFirebase.findTenantByCode(code);
      if (!existing) return code;
    }
    // Fall back to a longer code — collisions at this rate are implausible.
    return generateReferralCode(8);
  },

  /**
   * Look up a tenant by its referral code. Reads the public
   * `referralCodes/{CODE}` index — the only collection an unrelated
   * signup flow is allowed to hit. Returns
   *   { id, name, referralCode }
   * or `null` if the code is not found / malformed.
   */
  async findTenantByCode(code) {
    const normalised = normaliseReferralCode(code);
    if (!normalised) return null;
    try {
      const snap = await getDoc(doc(db, CODES, normalised));
      if (!snap.exists()) return null;
      const data = snap.data() || {};
      return {
        id: data.tenantId,
        name: data.tenantName || null,
        referralCode: normalised,
      };
    } catch (err) {
      console.warn('referralServiceFirebase.findTenantByCode failed:', err.message);
      return null;
    }
  },

  /**
   * Write the tenant's own code onto its document AND publish the
   * public lookup row. Safe to call during bootstrap — merges against
   * an existing tenant doc.
   */
  async assignCodeToTenant(tenantId, code, { tenantName = null } = {}) {
    if (!tenantId || !code) return;
    const normalised = normaliseReferralCode(code);
    await setDoc(
      doc(db, TENANTS, tenantId),
      { referralCode: normalised, referralCodeAssignedAt: Timestamp.now() },
      { merge: true },
    );
    await setDoc(doc(db, CODES, normalised), {
      tenantId,
      tenantName,
      createdAt: Timestamp.now(),
    });
  },

  /**
   * Record a pending referral. Called by the signup flow as soon as the
   * new tenant exists. Idempotent: one pending doc per (referrer,
   * referred) pair — we never want to double-count if signup retries.
   */
  async recordPending({ referrerTenantId, referredTenantId, referralCode }) {
    if (!referrerTenantId || !referredTenantId) return null;
    if (referrerTenantId === referredTenantId) return null; // self-ref guard
    try {
      const existing = await getDocs(
        query(
          collection(db, REFERRALS),
          where('referrerTenantId', '==', referrerTenantId),
          where('referredTenantId', '==', referredTenantId),
        ),
      );
      if (!existing.empty) {
        const first = existing.docs[0];
        return { id: first.id, ...first.data(), alreadyExisted: true };
      }
      const payload = {
        referrerTenantId,
        referredTenantId,
        referralCode: normaliseReferralCode(referralCode) || null,
        status: 'pending',
        createdAt: Timestamp.now(),
        convertedAt: null,
      };
      const ref = await addDoc(collection(db, REFERRALS), payload);
      return { id: ref.id, ...payload, alreadyExisted: false };
    } catch (err) {
      console.warn('referralServiceFirebase.recordPending failed:', err.message);
      return null;
    }
  },

  /**
   * List every referral this tenant sent out (used by the Referrals tab
   * in TenantAdmin to compute pending/paying counts).
   */
  async listByReferrer(referrerTenantId) {
    if (!referrerTenantId) return [];
    try {
      const q = query(
        collection(db, REFERRALS),
        where('referrerTenantId', '==', referrerTenantId),
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (err) {
      console.warn('referralServiceFirebase.listByReferrer failed:', err.message);
      return [];
    }
  },

  /**
   * Summary stats for the admin UI. Pure bucket count — the reward
   * logic ("3 paying → 6 months free") lives server-side, this only
   * reports what's visible.
   */
  async getStats(referrerTenantId) {
    const rows = await referralServiceFirebase.listByReferrer(referrerTenantId);
    let pending = 0;
    let paying = 0;
    let cancelled = 0;
    for (const r of rows) {
      if (r.status === 'paying') paying += 1;
      else if (r.status === 'cancelled') cancelled += 1;
      else pending += 1;
    }
    return { total: rows.length, pending, paying, cancelled };
  },

  /**
   * Fetch a tenant's own code — generating and persisting one if missing.
   * Admin console calls this on first load of the Referrals tab.
   */
  async ensureTenantCode(tenantId) {
    if (!tenantId) return null;
    const snap = await getDoc(doc(db, TENANTS, tenantId));
    if (!snap.exists()) return null;
    const data = snap.data();
    if (data.referralCode) return data.referralCode;
    const code = await referralServiceFirebase.pickUniqueCode();
    await referralServiceFirebase.assignCodeToTenant(tenantId, code, {
      tenantName: data.name || null,
    });
    return code;
  },
};

// Phase 2.10 selector — switches at module load.
export const referralService = AUTH_BACKEND === 'api' ? referralServiceV2 : referralServiceFirebase;

export default referralService;
